import { type FilterSpecification, featureFilter } from "@maplibre/maplibre-gl-style-spec";
import { z } from "zod";

/**
 * Which things something applies to, as a filter expression of the Mapbox and MapLibre style
 * specifications, evaluated by MapLibre's own implementation: true or false for each thing, read
 * from its `["id"]`, its `["get", name]` properties, and its `["geometry-type"]`: `Point` for a
 * point, `LineString` for a line, and `Polygon` for a shape.
 * `["in", ["id"], ["literal", ["u:de-vii", "u:de-ix"]]]` holds for two things, and
 * `["==", ["get", "side"], "axis"]` for every thing whose `side` is `axis`.
 */
export const FilterSchema = z
  .array(z.unknown())
  .min(1)
  .superRefine((filter, context) => {
    try {
      featureFilter(filter as FilterSpecification, "filter");
    } catch (error) {
      context.addIssue({
        code: "custom",
        message: `Not a filter expression: ${error instanceof Error ? error.message : String(error)}`,
      });
    }
  })
  .meta({
    description:
      'A Mapbox style-spec filter expression over each thing\'s ["id"], ["get", name] properties and ["geometry-type"] (Point, LineString or Polygon), such as ["in", ["id"], ["literal", ["u:de-vii", "u:de-ix"]]].',
  });
export type Filter = z.infer<typeof FilterSchema>;

/** A filter holding for the things with these ids: `["in", ["id"], ["literal", ids]]`. */
export const filterOfIds = (ids: readonly string[]): Filter => [
  "in",
  ["id"],
  ["literal", [...ids]],
];

/** The ids a filter holds for when all it does is list them, as `filterOfIds` writes it; or null. */
export function idsOfFilter(filter: Filter): string[] | null {
  const [operator, of, list] = filter;
  if (operator !== "in" || filter.length !== 3 || JSON.stringify(of) !== '["id"]') return null;
  if (!Array.isArray(list) || list.length !== 2 || list[0] !== "literal") return null;
  const ids = list[1];
  return Array.isArray(ids) && ids.every(id => typeof id === "string") ? ids : null;
}

/** What a filter reads of a thing: its id, its kind, and its properties. */
export interface Filterable {
  id: string;
  kind: string;
  properties?: Record<string, unknown>;
}

const GEOMETRY_TYPES: Record<string, 1 | 2 | 3> = { point: 1, line: 2, shape: 3 };
const compiled = new WeakMap<Filter, (thing: Filterable) => boolean>();

/** Whether a filter holds for a thing. Each filter is compiled once. */
export function filterMatches(filter: Filter, thing: Filterable): boolean {
  let test = compiled.get(filter);
  if (!test) {
    const { filter: holds } = featureFilter(filter as FilterSpecification, "filter");
    test = ({ id, kind, properties }) =>
      holds({ zoom: 0 }, { id, type: GEOMETRY_TYPES[kind] ?? 0, properties: properties ?? {} });
    compiled.set(filter, test);
  }
  return test(thing);
}
